import { ExerciseThumbnail } from "../../design-v2";

// Tier-3 builder presentation chrome shared by the Design V2 block editors
// (Phase 7A; card header reworked in the post-acceptance visual refinement).
// These are the repeated visual rules of a block card -- the drag activator,
// the icon actions, the collapsed card header and the expanded secondary
// action row -- not product concepts, so they stay internal to the builder
// package rather than becoming Tier-1 primitives.

// The collapsed card leads with its thumbnail. Both sizes reserve their square
// through the canonical `ExerciseThumbnail`, so a missing or failing image
// costs no layout shift. The lane size is smaller because a superset lane is
// nested one level deeper and still has to hold a readable name at 320px.
export const BLOCK_THUMBNAIL_SIZE = 56;
export const LANE_THUMBNAIL_SIZE = 48;

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

export function BlockIconButton({
  label,
  onClick,
  disabled = false,
  tone = "default",
  expanded,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-expanded={expanded}
      className={joinClassNames(
        "lz-builder-block__action",
        tone === "danger" && "lz-builder-block__action--danger"
      )}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}

export function ChevronPath({ collapsed }) {
  return <path d={collapsed ? "M4 6.5 8 10.5l4-4" : "M4 9.5 8 5.5l4 4"} />;
}

// One block thumbnail. Placeholder-first, exactly as the exercise-pool search
// rows are: no media source is wired anywhere in the app yet, so this renders
// the canonical tonal placeholder and carries the deterministic object key for
// whenever assets are attached (blueprint D28 / section 9.3). It never infers
// or fabricates an image URL.
export function BlockThumbnail({ exerciseId, size = BLOCK_THUMBNAIL_SIZE, className = "" }) {
  return (
    <ExerciseThumbnail
      exerciseId={exerciseId || undefined}
      src={null}
      size={size}
      alt=""
      loading="lazy"
      className={joinClassNames("lz-builder-block__thumbnail", className)}
    />
  );
}

// The collapsed card header: thumbnail, identity, then the two always-visible
// affordances side by side and vertically centred. They stay separate,
// full-target buttons -- neither may be dropped or merged into an ambiguous
// tap area -- but sitting them on one row rather than stacking them keeps the
// card short.
export function BlockCardHeader({
  dragHandle,
  thumbnail,
  eyebrow,
  title,
  meta,
  isCollapsed,
  toggleLabel,
  onToggle,
}) {
  return (
    <div className="lz-builder-block__header">
      {thumbnail}

      <div className="lz-builder-block__identity">
        {eyebrow ? <p className="lz-builder-block__eyebrow">{eyebrow}</p> : null}
        <h3 className="lz-builder-block__title">{title}</h3>
        {meta}
      </div>

      <div className="lz-builder-block__header-controls">
        {dragHandle}
        <BlockIconButton
          label={toggleLabel}
          expanded={!isCollapsed}
          onClick={onToggle}
        >
          <ChevronPath collapsed={isCollapsed} />
        </BlockIconButton>
      </div>
    </div>
  );
}

// Secondary and destructive actions live at the END of the expanded body, so
// they are never easier to reach than the expand toggle and never crowd the
// collapsed card. Labelled text buttons, not icon-only ones.
// `trailing` is the icon-only destructive action. It is a sibling of the
// labelled group rather than one more item in it, so it always keeps the end of
// the row: when the labelled actions cannot share a line -- two long uppercase
// labels at 320px -- they wrap between themselves and the icon stays put,
// instead of being orphaned onto a line of its own.
export function BlockBodyActions({ children, trailing = null }) {
  return (
    <div className="lz-builder-block__body-actions">
      <div className="lz-builder-block__body-actions-group">{children}</div>
      {trailing}
    </div>
  );
}

// `iconOnly` is for the destructive action at the end of the row. It keeps the
// full accessible name on the button itself, so the control is never identified
// by a tooltip alone, and it stays a fixed square while the labelled structural
// actions share the remaining width.
export function BlockBodyAction({
  label,
  shortLabel,
  onClick,
  disabled = false,
  tone = "default",
  iconOnly = false,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // The accessible name is the full label in every case: an icon-only
      // action carries it as `aria-label`, and a responsive action keeps the
      // long form in the accessibility tree even when the narrow viewport is
      // showing the short one.
      aria-label={iconOnly || shortLabel ? label : undefined}
      className={joinClassNames(
        "lz-builder-block__body-action",
        tone === "danger" && "lz-builder-block__body-action--danger",
        iconOnly && "lz-builder-block__body-action--icon"
      )}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        {children}
      </svg>
      {iconOnly ? null : (
        <span className="lz-builder-block__body-action-label" aria-hidden={shortLabel ? "true" : undefined}>
          {/* Two spans, one shown per width by CSS -- never a font-size or
              target-size compromise, and never a second command row. */}
          <span className="lz-builder-block__body-action-text">{label}</span>
          {shortLabel ? (
            <span className="lz-builder-block__body-action-text--short">
              {shortLabel}
            </span>
          ) : null}
        </span>
      )}
    </button>
  );
}

// The two destructive/structural glyphs shared by every block action bar, so a
// duplicate reads the same whether it sits on a single, a superset or cardio.
export function TrashPath() {
  return <path d="M3 4.5h10M6.5 4.5V3h3v1.5M5 4.5l.6 8h4.8l.6-8" />;
}

export function DuplicatePath() {
  return (
    <>
      <path d="M5.5 5.5h7v7h-7z" />
      <path d="M3.5 10.5v-7h7" />
    </>
  );
}

// The activator keeps dnd-kit's listeners and `touch-action: none`; the
// container still owns the sensors and their activation constraints.
export default function BlockDragHandle({
  disabled = false,
  setHandleRef,
  handleAttributes,
  handleListeners,
}) {
  return (
    <button
      type="button"
      ref={setHandleRef}
      disabled={disabled}
      {...handleAttributes}
      {...handleListeners}
      className="lz-builder-block__handle"
      aria-label="Reorder block"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M2.5 5.5h11M2.5 10.5h11" />
      </svg>
    </button>
  );
}
